// ============================================
// FILE: src/utils/imageHelpers.js
// ============================================

const getBackendOrigin = () => {
  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

  try {
    if (apiUrl.startsWith('http://') || apiUrl.startsWith('https://')) {
      return new URL(apiUrl).origin;
    }

    if (typeof window !== 'undefined') {
      return new URL(apiUrl, window.location.origin).origin;
    }
  } catch {
    // Fall back to localhost for malformed or missing URL config.
  }

  return 'http://localhost:8000';
};

export const resolveProductImageUrl = (rawImageUrl) => {
  if (!rawImageUrl || typeof rawImageUrl !== 'string') {
    return null;
  }

  const imageUrl = rawImageUrl.trim();
  if (!imageUrl || imageUrl === 'null' || imageUrl.includes('�')) {
    return null;
  }

  if (/^(https?:)?\/\//i.test(imageUrl)) {
    return imageUrl.startsWith('//') ? `https:${imageUrl}` : imageUrl;
  }

  if (/^(data:|blob:)/i.test(imageUrl)) {
    return imageUrl;
  }

  const backendOrigin = getBackendOrigin();

  if (imageUrl.startsWith('/')) {
    return `${backendOrigin}${imageUrl}`;
  }

  if (imageUrl.startsWith('uploads/') || imageUrl.startsWith('storage/')) {
    return `${backendOrigin}/${imageUrl}`;
  }

  return imageUrl;
};

/**
 * Shared utility function to get product image with fallback to emoji
 * Used across HomeScreen, ProductDetailsModal, OrdersScreen, and OrderConfirmationModal
 */
export const getProductImage = (product, size = 'medium') => {
  // Check all known backend/frontend image fields in priority order.
  const rawImageUrl =
    product?.featured_image_url ||
    product?.image_url ||
    product?.featured_image ||
    product?.product_image ||
    product?.thumbnail_url ||
    product?.thumbnail ||
    product?.image;

  const fullImageUrl = resolveProductImageUrl(rawImageUrl);

  // If product has a valid image URL, return img element.
  if (fullImageUrl) {
    
    // Define size classes
    const sizeClasses = {
      small: 'w-8 h-8',
      medium: 'w-12 h-12', 
      large: 'w-16 h-16',
      xl: 'w-20 h-20',
      modal: 'w-full h-64'
    };
    
    if (size === 'card') {
      return (
        <>
          <img
            src={fullImageUrl}
            alt={product?.name || 'Product'}
            className="absolute inset-0 w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
            onError={(e) => {
              e.target.style.display = 'none';
              if (e.target.nextSibling) e.target.nextSibling.style.display = 'flex';
            }}
          />
          <div className="absolute inset-0 w-full h-full bg-gradient-to-br from-blue-50 to-blue-100 items-center justify-center" style={{ display: 'none' }}>
            <span className="text-5xl">🥛</span>
          </div>
        </>
      );
    }

    if (size === 'modal') {
      return (
        <div className="relative w-full h-72 sm:h-80 bg-gray-50 rounded-xl overflow-hidden flex items-center justify-center">
          <img
            src={fullImageUrl}
            alt={product?.name || 'Product'}
            className="max-w-full max-h-full w-auto h-auto object-contain"
            onError={(e) => {
              // Fallback to emoji if image fails to load
              e.target.style.display = 'none';
              if (e.target.nextSibling) {
                e.target.nextSibling.style.display = 'flex';
              }
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl items-center justify-center" style={{display: 'none'}}>
            <span className="text-8xl">{getProductEmoji(product, 'xl')}</span>
          </div>
        </div>
      );
    }
    
    return (
      <img 
        src={fullImageUrl}
        alt={product?.name || 'Product'}
        className={`${sizeClasses[size]} object-cover rounded-lg bg-gray-100`}
        onError={(e) => {
          // Fallback to emoji if image fails to load
          e.target.style.display = 'none';
          if (e.target.nextSibling) {
            e.target.nextSibling.style.display = 'flex';
          }
        }}
      />
    );
  }
  
  // Return emoji directly if no image URL
  return getProductEmoji(product, size);
};

/**
 * Get product emoji with size styling
 */
export const getProductEmoji = (product, size = 'medium') => {
  const sizeClasses = {
    small: 'w-8 h-8 text-lg',
    medium: 'w-12 h-12 text-2xl', 
    large: 'w-16 h-16 text-3xl',
    xl: 'w-20 h-20 text-4xl',
    modal: 'w-full h-64 text-8xl'
  };
  
  // Determine emoji based on product name or default fallback
  let emoji = '🥛'; // Default milk emoji
  
  if (product?.name || product?.product_name) {
    const name = (product.name || product.product_name).toLowerCase();
    
    if (name.includes('milk')) {
      emoji = '🥛';
    } else if (name.includes('butter') || name.includes('cheese')) {
      emoji = '🧈';
    } else if (name.includes('yogurt') || name.includes('curd')) {
      emoji = '🍯';
    } else if (name.includes('cream')) {
      emoji = '🥛';
    } else if (name.includes('lassi')) {
      emoji = '🥤';
    }
  }
  
  if (size === 'card') {
    return (
      <div className="absolute inset-0 w-full h-full bg-gradient-to-br from-blue-50 to-blue-100 flex items-center justify-center">
        <span className="text-5xl">{emoji}</span>
      </div>
    );
  }

  const containerClass = size === 'modal' ? 'w-full h-64 bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl flex items-center justify-center' : `${sizeClasses[size]} flex items-center justify-center bg-gray-100 rounded-lg`;

  return (
    <div className={containerClass}>
      <span>{emoji}</span>
    </div>
  );
};

/**
 * Get product image for order items (handles different data structures)
 */
export const getOrderItemImage = (item, size = 'medium') => {
  // Try to construct a product object from order item
  const product = {
    name: item.name || item.product_name || item.product?.name,
    featured_image_url: item.featured_image_url || item.product?.featured_image_url || item.product_image_url || item.product?.product_image_url,
    image_url: item.image_url || item.product?.image_url || item.product_image,
    featured_image: item.featured_image || item.product?.featured_image,
    product_image: item.product_image || item.product?.product_image,
    thumbnail_url: item.thumbnail_url || item.product?.thumbnail_url,
    thumbnail: item.thumbnail || item.product?.thumbnail,
    image: item.image || item.product?.image
  };
  
  return getProductImage(product, size);
};